import { Feature, Setting } from '@weave/schema-gen-ts/dist/schemas/search/v1/service.pb';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { getUser } from '@frontend/auth-helpers';
import { ContactsGlobalSearchModule } from '@frontend/contacts-global-search-module';
import { FaxSearchFilters, FaxesGlobalSearchModule } from '@frontend/fax';
import { GlobalSearch, useGlobalSearch } from '@frontend/global-search';
import { MessagesFilters, MessagesGlobalSearchModule, useInboxSearch } from '@frontend/messages-global-search-module';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatResultsPrimaryKeys, handleUpdateSearch } from '@frontend/search-history';
import { SearchQueries, useFilterSettings } from '@frontend/search-hooks';
import { pendo } from '@frontend/tracking';
import { FeatureGlobalSearchModule, FeatureGlobalSearchModuleOld } from '../feature-global-search-module';
import { SettingsGlobalSearchModule, SettingsGlobalSearchModuleOld } from '../settings-global-search-module';
import { useFilterFeatures } from './use-filter-features';

/**
 * This is where all GlobalSearch modules should be placed. The `id` prop which is passed to the `GlobalSearch.Section` component is used to 
 * determine which module to display based on the current search context. The `GlobalSearch.Section` component will only display the module
 * if the `id` prop matches the current search context.
 * 
 * Same goes for the `GlobalSearch.Filters.Section` component. The `id` prop is used to determine which filter to display based on the current
 * search context. The recommended way to handle filters is to use `createProvider` from `@frontend/store`, and then wrap it around both 
 * GlobalSearch.Filters and GlobalSearch.Content components. This way, the filters will be able to update the search context and the content
 * will be able to display the correct module based on the search context.
 * 
 * Example:
 * ```ts
 * export const GlobalSearchView = () => {
  return (
    <GlobalSearch>
     <MessagesFilterProvider>
      <GlobalSearch.Filters>
       ....
      </GlobalSearch.Filters>
      <GlobalSearch.Content>
       ....
      </GlobalSearch.Content>
     </MessagesFilterProvider>
    </GlobalSearch>
  );
};
 */

export type GlobalSearchViewProps = { className?: string };

export const GlobalSearchView = ({ className }: GlobalSearchViewProps) => {
  return (
    <GlobalSearch className={className}>
      <GlobalSearchViewContents />
    </GlobalSearch>
  );
};

const GlobalSearchViewContents = () => {
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasFaxSearch = getFeatureFlagValue('fax-global-search');
  const inboxSearch = useInboxSearch();
  return (
    <>
      <GlobalSearch.Filters>
        <GlobalSearch.Filters.Section id='messaging'>
          <MessagesFilters
            filters={inboxSearch.filters}
            setFilters={inboxSearch.setFilters}
            hideFilters={inboxSearch.hideFilters}
          />
        </GlobalSearch.Filters.Section>
        {hasFaxSearch && (
          <GlobalSearch.Filters.Section id='faxes'>
            <FaxSearchFilters />
          </GlobalSearch.Filters.Section>
        )}
      </GlobalSearch.Filters>
      <GlobalSearch.Content>
        {/* If the order or modules within this changes, update the GlobalSearchModuleOrder object */}
        <GlobalSearch.Section id='contacts'>
          <ContactsGlobalSearchModule />
        </GlobalSearch.Section>
        {hasFaxSearch && (
          <GlobalSearch.Section id='faxes'>
            <FaxesGlobalSearchModule />
          </GlobalSearch.Section>
        )}
        <GlobalSearch.Section id='messaging'>
          <MessagesGlobalSearchModule {...inboxSearch} />
        </GlobalSearch.Section>
        <GlobalSearchSettingsAndFeatures />
      </GlobalSearch.Content>
    </>
  );
};

// separate module to utilize the `useGlobalSearch` hook and call the search endpoint
const GlobalSearchSettingsAndFeatures = () => {
  const { debouncedSearchTerm } = useGlobalSearch(['debouncedSearchTerm', 'viewAllFeature', 'close']);
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasSemanticSearch = getFeatureFlagValue('semantic-search');
  const { filterFeatures } = useFilterFeatures();
  const { filterSetting } = useFilterSettings();
  const userID = getUser()?.userID || '';

  const {
    data: searchResults,
    isLoading,
    isError,
  } = SearchQueries.useSearch(
    {
      entity: [Entity.ENTITY_FEATURE, Entity.ENTITY_SETTING],
      query: debouncedSearchTerm,
    },
    {
      enabled: debouncedSearchTerm.length > 0 && hasSemanticSearch,
      onSettled: (response) => {
        const searchedFeatures = response?.feature ?? [];
        // Filter features based on accessible routes
        const filteredFeatures = filterFeatures(searchedFeatures);
        handleUpdateSearch({
          entity: Entity.ENTITY_FEATURE,
          results: formatResultsPrimaryKeys<Feature>('path', filteredFeatures),
        });

        const searchedSettings = response?.setting ?? [];
        // Filter settings based on user permissions and feature flags
        const filteredSettings = filterSetting(searchedSettings);
        handleUpdateSearch({
          entity: Entity.ENTITY_SETTING,
          results: formatResultsPrimaryKeys<Setting>('path', filteredSettings),
        });

        // Track the search event with Pendo
        pendo.track('global_semantic_search_query', {
          entity: [Entity.ENTITY_FEATURE, Entity.ENTITY_SETTING],
          query: debouncedSearchTerm,
          timestamp: new Date().toISOString(),
          userId: userID,
        });
      },
    }
  );

  const { feature: searchedFeatures = [] } = searchResults || {};
  const { setting: searchedSettings = [] } = searchResults || {};

  const filteredFeatures = filterFeatures(searchedFeatures);
  const filteredSettings = filterSetting(searchedSettings);

  // TODO: remove this if block when semantic-search FF is fully implemented
  // just for a fallback in case the search service is not available, we are going to show the old search component
  if (!hasSemanticSearch || isError) {
    return (
      <>
        <GlobalSearch.Section id='settings'>
          <SettingsGlobalSearchModuleOld />
        </GlobalSearch.Section>
        <GlobalSearch.Section id='features'>
          <FeatureGlobalSearchModuleOld />
        </GlobalSearch.Section>
      </>
    );
  }

  return (
    <>
      <GlobalSearch.Section id='settings'>
        <SettingsGlobalSearchModule isLoading={isLoading} searchedSettings={filteredSettings} />
      </GlobalSearch.Section>
      <GlobalSearch.Section id='features'>
        <FeatureGlobalSearchModule isLoading={isLoading} searchedFeatures={filteredFeatures} />
      </GlobalSearch.Section>
    </>
  );
};
